import { resolveAudioTrackPlayback, type ProjectAudioTrack } from "./types";

/** Neither half may be shorter than this — a sliver is not a track. */
export const MIN_SPLIT_PART_MS = 150;

/**
 * Split one lane track in two at a point inside its kept window.
 *
 * Both halves keep the SAME blob and only move their trim window, so nothing is
 * re-encoded and nothing is copied: a split is a bookkeeping change, and the
 * audio a listener hears is byte-identical either side of the cut. The second
 * half starts in output time where the first one ends, which is what keeps the
 * lane sounding unchanged the moment after you split it — a split is meant to
 * let you move a piece, not to alter what plays.
 *
 * `atMs` is measured in the TRACK's own time, matching the trim handles and the
 * waveform, not in output time. The caller converts.
 */
export function splitAudioTrack(
  track: ProjectAudioTrack,
  playlist: { fadeIn: boolean; fadeOut: boolean },
  atMs: number,
  newId: () => string,
): [ProjectAudioTrack, ProjectAudioTrack] | null {
  const played = resolveAudioTrackPlayback(track, playlist);
  const cut = Math.round(atMs);
  // Refused rather than clamped: clamping a cut outside the window would put it
  // somewhere the operator did not point at.
  if (cut - played.trimStartMs < MIN_SPLIT_PART_MS) return null;
  if (played.trimEndMs - cut < MIN_SPLIT_PART_MS) return null;

  const startMs = track.startMs;
  const first: ProjectAudioTrack = {
    ...track,
    trimStartMs: played.trimStartMs,
    trimEndMs: cut,
    // The tail carries the fade-out; the head keeps the fade-in. A fade at the
    // cut would be an edit nobody asked for, audible as a dip mid-phrase.
    fadeIn: played.fadeIn,
    fadeOut: false,
  };
  const second: ProjectAudioTrack = {
    ...track,
    id: newId(),
    trimStartMs: cut,
    trimEndMs: played.trimEndMs,
    fadeIn: false,
    fadeOut: played.fadeOut,
    // Only when the first half has a position: a legacy sequential track must
    // stay legacy, or the lane's whole reading changes underneath it.
    ...(startMs !== undefined
      ? { startMs: startMs + (cut - played.trimStartMs) }
      : {}),
  };
  return [first, second];
}
